import type { QuestionType, LayoutTemplate, TemplateDefinition } from '../types/outreach';
import { PARTY_SOS_OPTIONS } from './parties';

export const QUESTION_TYPES: { value: QuestionType; label: string }[] = [
  { value: 'short_text', label: 'Short Text' },
  { value: 'long_text', label: 'Long Text' },
  { value: 'single_select', label: 'Single Select' },
  { value: 'multi_select', label: 'Multi Select' },
  { value: 'nps', label: 'NPS (0-10)' },
  { value: 'numeric', label: 'Numeric' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' }
];

export const DEMOGRAPHIC_FIELDS: { id: string; label: string; description?: string; isSpecial?: boolean }[] = [
  { id: 'first_name', label: 'First Name' },
  { id: 'last_name', label: 'Last Name' },
  { id: 'email', label: 'Email' },
  { id: 'phone', label: 'Phone' },
  { id: 'district', label: 'District (Address + Legislative Districts)', description: 'Includes address lookup and captures US House, State Senate, State House, County, Municipality', isSpecial: true },
  { id: 'organization', label: 'Organization' },
  { id: 'age_range', label: 'Age Range' },
  { id: 'party_affiliation', label: 'Party Affiliation' },
  { id: 'gender', label: 'Gender' }
];

/**
 * District is collected as ONE demographic field but stored as five values —
 * `demographics.district = { us_house, state_senate, state_house, county,
 * municipality }` (outreachGeocodingService.lookupDistricts).
 *
 * Reporting treats each part as its own field: one nested object cannot be a
 * column, a chart axis, or a filter, which is why district used to be dropped
 * from every reporting surface. The dotted ids below resolve through the same
 * `demo_` path both the frontend and backend answer-readers understand.
 *
 * Must stay identical to DISTRICT_PARTS in
 * backend/src/routes/outreach-analytics.js.
 */
export const DISTRICT_PARTS: { key: string; label: string }[] = [
  { key: 'us_house', label: 'US House District' },
  { key: 'state_senate', label: 'State Senate District' },
  { key: 'state_house', label: 'State House District' },
  { key: 'county', label: 'County' },
  { key: 'municipality', label: 'Municipality' },
];

export const AGE_RANGE_OPTIONS = [
  { value: '18-24', label: '18-24' },
  { value: '25-34', label: '25-34' },
  { value: '35-44', label: '35-44' },
  { value: '45-54', label: '45-54' },
  { value: '55-64', label: '55-64' },
  { value: '65+', label: '65+' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

export const PARTY_AFFILIATION_OPTIONS = [...PARTY_SOS_OPTIONS];

export const GENDER_OPTIONS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'not disclosed', label: 'Not Disclosed' },
  { value: 'x', label: 'X' },
];

export const FONT_OPTIONS = [
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins',
  'Source Sans Pro', 'Nunito', 'Raleway', 'Work Sans',
  'Arial', 'Georgia', 'Times New Roman'
];

export const LAYOUT_TEMPLATES: { value: LayoutTemplate; label: string; description: string }[] = [
  { value: 'modern', label: 'Modern', description: 'Clean, centered layout with card design' },
  { value: 'corporate', label: 'Corporate', description: 'Professional with prominent header/footer' },
  { value: 'minimal', label: 'Minimal', description: 'Ultra-simple, content-focused design' },
  { value: 'branded', label: 'Branded', description: 'Full branding with custom HTML support' }
];

export const DEFAULT_DEFINITION: TemplateDefinition = {
  questions: [],
  demographics: {
    enabled: false,
    required_fields: [],
    optional_fields: []
  },
  post_submission_message: '<p>Thank you for your response!</p>',
  branding: {
    logos: {
      organization: {
        enabled: false,
        url: null,
        position: 'header_center'
      },
      workspace: {
        enabled: false,
        url: null,
        position: 'header_left'
      }
    },
    colors: {
      primary: '#2563eb',
      secondary: '#64748b',
      background: '#ffffff',
      text: '#1e293b'
    },
    fonts: {
      heading: 'Inter',
      body: 'Inter'
    },
    layout_template: 'modern',
    custom_header_html: null,
    custom_footer_html: null
  }
};
